import React, { useState, useEffect, useCallback } from 'react';
import CompanyPanel from '../components/CompanyPanel';
import ContactPanel from '../components/ContactPanel';
import API_BASE from '../config';
import FilterRail from '../components/FilterRail';
import { CONTACT_SCHEMA, COMPANY_SCHEMA } from '../components/filterSchema';
import { useParams, useNavigate } from 'react-router-dom';
import {useUser, useAuth } from '@clerk/clerk-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import toast from "react-hot-toast";
import { creditAwareFetch, emitCreditsChanged } from '../hooks/creditAwareFetch';

const SECTORS = ['Technology, Information and Media','Financial Services','Hospitals and Health Care','Manufacturing','Professional Services','Consumer Services','Retail','Education','Real Estate and Equipment Rental','Construction','Transportation, Logistics, Supply Chain and Storage','Energy, Mining and Utilities','Government Administration','Non-profit Organizations','Entertainment Providers','Accommodation and Food Services','Administrative and Support Services','Farming, Ranching and Forestry','Civic and Social Organizations','International Affairs','Public Safety','Holding Companies','Religious Institutions','Other'];
const CONTACT_STATUSES = ['new','contacted','replied','meeting booked','closed'];
const STATUS_COLOURS = { new: '#888', contacted: '#2B6CB0', replied: '#B7791F', 'meeting booked': '#276749', closed: '#4A5568' };
const STATUS_BG = { new: '#F7F7F7', contacted: '#EBF8FF', replied: '#FFFBEB', 'meeting booked': '#F0FFF4', closed: '#F7FAFC' };
const SIGNAL_LABELS = { funding: 'Funding Round', hiring: 'Hiring Activity', leadership_change: 'Leadership Change', news: 'Company News', tech_change: 'Technology Change' };
const SIGNAL_COLOURS = { funding: '#276749', hiring: '#2B6CB0', leadership_change: '#8B1A1A', news: '#B7791F', tech_change: '#553C9A' };
const SIGNAL_BG = { funding: '#F0FFF4', hiring: '#EBF8FF', leadership_change: '#FFF5F5', news: '#FFFFF0', tech_change: '#FAF5FF' };
const PAGE_SIZE = 25;
const ENRICH_LIMIT = 10;

const inp = { width: '100%', backgroundColor: '#fff', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.6rem 0.8rem', color: '#1A1A1A', fontSize: '0.875rem', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
const lbl = { display: 'block', fontSize: '0.8rem', color: '#4A5568', marginBottom: '0.35rem', fontWeight: '500' };

function mapRow(row) {
  const get = (...keys) => { for (const k of keys) { const found = Object.keys(row).find(rk => rk.trim().toLowerCase() === k.trim().toLowerCase()); if (found && row[found] !== undefined && row[found] !== null && String(row[found]).trim() !== '') return String(row[found]).trim(); } return ''; };
  const first = get('First Name','Contact First Name','FirstName');
  const last = get('Last Name','Contact Last Name','LastName');
  return { company_name: get('Company Name','Company Name for Emails','Company','Account','Organisation','Organization'), website: get('Website','Company Website','Domain'), sector: get('Sector','Industry','Company Industry'), size: get('Number of Employees','Employees','Company Size','HeadCount'), country: get('Country','Company Country','Location','HQ Country'), company_linkedin_url: get('Company Linkedin Url','Company LinkedIn','LinkedIn Company'), keywords: get('Keywords','Tags','Company Keywords'), revenue: get('Revenue','Annual Revenue','Company Revenue'), full_name: [first, last].filter(Boolean).join(' ') || get('Name','Full Name','Contact Name'), job_title: get('Title','Job Title','Position','Role'), email: get('Email','Email Address','Work Email','Contact Email'), linkedin_url: get('Person Linkedin Url','LinkedIn Url','LinkedIn','Contact LinkedIn','LinkedIn URL'), phone: get('Phone','Phone Number','Mobile Phone','Direct Phone'), notes: get('Notes','Comments','Description') };
}

// ── Contact Detail Panel ─────────────────────────────────────────────────────
function Prospects() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { getToken } = useAuth();
  const [client, setClient] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyPanel, setCompanyPanel] = useState(null);
  const [sweepMeta, setSweepMeta] = useState({});
  const [detectingSignals, setDetectingSignals] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [contacts, setContacts] = useState([]);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importPreview, setImportPreview] = useState(null); // dry-run result
  const [importStrategy, setImportStrategy] = useState('skip');
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState([]);
  const [contactSearching, setContactSearching] = useState(false);
  const [companyForm, setCompanyForm] = useState({ name: '', website: '', sector: '', size: '', location: '', linkedin_url: '', revenue: '' });
  const [nameCheck, setNameCheck] = useState({ checking: false, canonical: null });
  const checkCompanyName = async (website) => {
    if (!website || !website.trim()) { setNameCheck({ checking: false, canonical: null }); return; }
    setNameCheck({ checking: true, canonical: null });
    try {
      const r = await fetch(`${API_BASE}/companies/check-name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ website })
      });
      const data = await r.json();
      setNameCheck({ checking: false, canonical: data.canonical_name || null });
    } catch (_) {
      setNameCheck({ checking: false, canonical: null });
    }
  };
  const [contactForm, setContactForm] = useState({ full_name: '', job_title: '', email: '', linkedin_url: '', phone: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [enrichedContacts, setEnrichedContacts] = useState({});
  const [enriching, setEnriching] = useState(null);
  const [viewingEnrichment, setViewingEnrichment] = useState(null);
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [bulkEnrichProgress, setBulkEnrichProgress] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchMode, setSearchMode] = useState('company');
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [selectedGlobalContactIds, setSelectedGlobalContactIds] = useState(new Set());
  const [addToBatchOpen, setAddToBatchOpen] = useState(false);
  const [addToBatchChannel, setAddToBatchChannel] = useState('linkedin');
  const [availableBatches, setAvailableBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [creatingNewBatch, setCreatingNewBatch] = useState(false);
  const [newBatchName, setNewBatchName] = useState('');
  const [newBatchDestination, setNewBatchDestination] = useState('pipedrive_csv');
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [addContactForm, setAddContactForm] = useState({ full_name: '', job_title: '', email: '', linkedin_url: '', phone: '', company_name: '' });
  const [addContactSaving, setAddContactSaving] = useState(false);
  const [addContactCompany, setAddContactCompany] = useState(null);
  const [addContactLookingUp, setAddContactLookingUp] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [allContactsLoading, setAllContactsLoading] = useState(false);
  const [contactFilters, setContactFilters] = useState({ score: null, has_sequence: null, email: null, linkedin: null, in_batch: null, enriched: null, has_linkedin_url: null, has_email: null });
  // Server-side God's-eye filter (new). Runs alongside the legacy client filter.
  const [sfTree, setSfTree] = useState(null);
  const [sfRows, setSfRows] = useState(null); // null = inactive, fall back to legacy list
  const [sfTotal, setSfTotal] = useState(0);
  const [sfFacets, setSfFacets] = useState(null);
  const [sfLoading, setSfLoading] = useState(false);
  const [sfPage, setSfPage] = useState(0);
  // company server-filter state (parallel to contact sf*)
  const [cfTree, setCfTree] = useState(null);
  const [cfRows, setCfRows] = useState(null);
  const [cfTotal, setCfTotal] = useState(0);
  const [cfLoading, setCfLoading] = useState(false);
  const [cfPage, setCfPage] = useState(0);
  const [companyViews, setCompanyViews] = useState([]);
  const [savedViews, setSavedViews] = useState([]);
  const [filterTargets, setFilterTargets] = useState({});
  const [filterDistincts, setFilterDistincts] = useState({});
  const [contactSequences, setContactSequences] = useState([]);
  const [contactChannels, setContactChannels] = useState([]);
  const [contactBatchMemberships, setContactBatchMemberships] = useState([]);
  const [contactScores, setContactScores] = useState([]);
  const [contactPage, setContactPage] = useState(0);

  useEffect(() => { fetchClient(); fetchCompanies(); fetchSweepStatus(); }, [id]);
  useEffect(() => {
    if (!search.trim()) { setFilteredCompanies(companies); setPage(0); return; }
    const q = search.toLowerCase();
    setFilteredCompanies(companies.filter(c => c.name?.toLowerCase().includes(q) || c.sector?.toLowerCase().includes(q) || c.location?.toLowerCase().includes(q)));
    setPage(0);
  }, [search, companies]);
  useEffect(() => { if (selectedCompany) fetchContacts(selectedCompany.id); }, [selectedCompany]);

  // Load all contacts when switching to contact mode
  useEffect(() => {
    if (searchMode === 'contact' && allContacts.length === 0) {
      setAllContactsLoading(true);
      Promise.all([
        fetch(`${API_BASE}/clients/${id}/contacts?limit=500`).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API_BASE}/clients/${id}/sequences`).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API_BASE}/clients/${id}/sequence-channels`).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API_BASE}/clients/${id}/batch-memberships`).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API_BASE}/clients/${id}/scores`).then(r => r.ok ? r.json() : []).catch(() => []),
      ]).then(([contacts, seqs, chans, memberships, scores]) => {
        setAllContacts(Array.isArray(contacts) ? contacts : []);
        setContactSequences(Array.isArray(seqs) ? seqs : []);
        setContactChannels(Array.isArray(chans) ? chans : []);
        setContactBatchMemberships(Array.isArray(memberships) ? memberships : []);
        setContactScores(Array.isArray(scores) ? scores : []);
      }).finally(() => setAllContactsLoading(false));
    }
  }, [searchMode, id]);

  // --- Server-side God's-eye filter wiring ---
  const SF_PAGE_SIZE = 25;
  const sfActive = sfTree && Array.isArray(sfTree.and) && sfTree.and.length > 0;

  const runServerFilter = useCallback(async (tree, page) => {
    if (!tree || !Array.isArray(tree.and) || tree.and.length === 0) {
      setSfRows(null); setSfTotal(0); setSfFacets(null); return;
    }
    setSfLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/filter/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          client_id: parseInt(id),
          tree,
          sort: { field: 'score', dir: 'desc' },
          limit: SF_PAGE_SIZE,
          offset: page * SF_PAGE_SIZE,
        }),
      });
      const data = await res.json();
      if (res.ok) { setSfRows(data.rows || []); setSfTotal(data.total || 0); }
      else { setSfRows([]); setSfTotal(0); }
    } catch (e) { setSfRows([]); setSfTotal(0); }
    finally { setSfLoading(false); }
  }, [id, getToken]);

  useEffect(() => { runServerFilter(sfTree, sfPage); }, [sfTree, sfPage, runServerFilter]);

  // Select-all that respects the active server filter: resolves the FULL matching id set
  // (uncapped) rather than the visible page or the unfiltered list.
  const selectAllContacts = useCallback(async () => {
    if (!sfActive) { setSelectedGlobalContactIds(new Set(allContacts.map(c => c.id))); return; }
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/bulk/resolve-ids`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ entity: 'contacts', client_id: parseInt(id), filter: sfTree }),
      });
      const data = await res.json();
      if (res.ok) setSelectedGlobalContactIds(new Set((data.ids || []).map(Number)));
    } catch (e) {}
  }, [sfActive, sfTree, id, getToken, allContacts]);

  const cfActive = cfTree && Array.isArray(cfTree.and) && cfTree.and.length > 0;
  const runCompanyFilter = useCallback(async (tree, page) => {
    if (!tree || !Array.isArray(tree.and) || tree.and.length === 0) { setCfRows(null); setCfTotal(0); return; }
    setCfLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/filter/companies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(id), tree, sort: { field: 'created_at', dir: 'desc' }, limit: SF_PAGE_SIZE, offset: page * SF_PAGE_SIZE }),
      });
      const data = await res.json();
      if (res.ok) { setCfRows(data.rows || []); setCfTotal(data.total || 0); }
      else { setCfRows([]); setCfTotal(0); }
    } catch (e) { setCfRows([]); setCfTotal(0); }
    finally { setCfLoading(false); }
  }, [id, getToken]);
  useEffect(() => { runCompanyFilter(cfTree, cfPage); }, [cfTree, cfPage, runCompanyFilter]);

  const selectAllCompanies = useCallback(async () => {
    if (!cfActive) { setSelectedIds(new Set(filteredCompanies.map(c => c.id))); return; }
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/bulk/resolve-ids`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ entity: 'companies', client_id: parseInt(id), filter: cfTree }),
      });
      const data = await res.json();
      if (res.ok) setSelectedIds(new Set((data.ids || []).map(Number)));
    } catch (e) {}
  }, [cfActive, cfTree, id, getToken, filteredCompanies]);

  const loadCompanyViews = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/saved-filters/companies?client_id=${parseInt(id)}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setCompanyViews(await res.json());
    } catch (e) {}
  }, [id, getToken]);
  useEffect(() => { loadCompanyViews(); }, [loadCompanyViews]);

  const loadSavedViews = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/saved-filters/contacts?client_id=${parseInt(id)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) setSavedViews(await res.json());
    } catch (e) {}
  }, [id, getToken]);
  useEffect(() => { loadSavedViews(); }, [loadSavedViews]);

  const loadFilterTargets = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/filter/contacts/targets/campaign?client_id=${parseInt(id)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setFilterTargets({ campaign: data }); }
    } catch (_) {}
  }, [id, getToken]);
  useEffect(() => { loadFilterTargets(); }, [loadFilterTargets]);

  const loadFilterDistincts = useCallback(async () => {
    try {
      const token = await getToken();
      const fields = (CONTACT_SCHEMA.fields || []).filter(f => f.dropdown).map(f => f.dropdown);
      const uniq = Array.from(new Set(fields));
      const out = {};
      await Promise.all(uniq.map(async (df) => {
        try {
          const res = await fetch(`${API_BASE}/filter/contacts/distinct/${encodeURIComponent(df)}?client_id=${parseInt(id)}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) out[df] = await res.json();
        } catch (_) {}
      }));
      setFilterDistincts(out);
    } catch (_) {}
  }, [id, getToken]);
  useEffect(() => { loadFilterDistincts(); }, [loadFilterDistincts]);

  async function saveCurrentView(name, shared) {
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/saved-filters/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(id), name, tree: sfTree, shared }),
      });
      loadSavedViews();
    } catch (e) {}
  }
  async function deleteView(viewId) {
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/saved-filters/${viewId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      loadSavedViews();
    } catch (e) {}
  }

  // Filter logic for global contacts list
  const seqByContact = new Map(contactSequences.map(s => [s.contact_id, s]));
  const channelByKey = new Map(contactChannels.map(ch => [ch.contact_id + ':' + ch.channel, ch]));
  const membershipByContact = new Map(contactBatchMemberships.map(m => [m.contact_id, m]));
  const scoreByContact = new Map(contactScores.map(s => [s.contact_id, s.total_score]));

  function getContactLinkedinUrl(ct) {
    if (ct.linkedin_url) return ct.linkedin_url;
    const ed = ct.enrichment_data;
    if (ed && typeof ed === 'object') return ed.linkedin_url || null;
    return null;
  }

  const matchesContactChips = (ct) => {
    const seq = seqByContact.get(ct.id);
    const linkedinCh = channelByKey.get(ct.id + ':linkedin');
    const emailCh = channelByKey.get(ct.id + ':email');
    const mb = membershipByContact.get(ct.id) || { in_linkedin_batch: false, in_email_batch: false };
    const score = scoreByContact.get(ct.id);
    const linkedinUrl = getContactLinkedinUrl(ct);

    if (contactFilters.score === 'high'     && !(score >= 8)) return false;
    if (contactFilters.score === 'mid'      && !(score >= 6 && score < 8)) return false;
    if (contactFilters.score === 'low'      && !(score < 6 && score != null)) return false;
    if (contactFilters.score === 'unscored' && score != null) return false;

    if (contactFilters.has_sequence === 'has'  && !seq) return false;
    if (contactFilters.has_sequence === 'none' && seq) return false;

    if (contactFilters.email === 'approved' && !(emailCh && emailCh.approval_status === 'approved')) return false;
    if (contactFilters.email === 'pending'  && !(emailCh && emailCh.approval_status === 'pending')) return false;
    if (contactFilters.email === 'none'     && emailCh) return false;

    if (contactFilters.linkedin === 'approved' && !(linkedinCh && linkedinCh.approval_status === 'approved')) return false;
    if (contactFilters.linkedin === 'pending'  && !(linkedinCh && linkedinCh.approval_status === 'pending')) return false;
    if (contactFilters.linkedin === 'none'     && linkedinCh) return false;

    if (contactFilters.in_batch === 'linkedin' && !mb.in_linkedin_batch) return false;
    if (contactFilters.in_batch === 'email'    && !mb.in_email_batch) return false;
    if (contactFilters.in_batch === 'none'     && (mb.in_linkedin_batch || mb.in_email_batch)) return false;

    if (contactFilters.enriched === 'yes' && !ct.enriched_at) return false;
    if (contactFilters.enriched === 'no'  && ct.enriched_at) return false;

    if (contactFilters.has_linkedin_url === 'yes' && !linkedinUrl) return false;
    if (contactFilters.has_linkedin_url === 'no'  && linkedinUrl) return false;

    if (contactFilters.has_email === 'yes' && !ct.email) return false;
    if (contactFilters.has_email === 'no'  && ct.email) return false;

    return true;
  };

  const filteredContacts = allContacts.filter(matchesContactChips);

  const prospectsFilters = [
    {
      key: 'score', label: 'Score',
      options: [
        { value: 'high',     label: '8+',       count: allContacts.filter(c => scoreByContact.get(c.id) >= 8).length },
        { value: 'mid',      label: '6–7',      count: allContacts.filter(c => { const s = scoreByContact.get(c.id); return s >= 6 && s < 8; }).length },
        { value: 'low',      label: '<6',       count: allContacts.filter(c => { const s = scoreByContact.get(c.id); return s != null && s < 6; }).length },
        { value: 'unscored', label: 'Unscored', count: allContacts.filter(c => scoreByContact.get(c.id) == null).length },
      ],
    },
    {
      key: 'has_sequence', label: 'Sequence',
      options: [
        { value: 'has',  label: 'Has sequence', count: allContacts.filter(c => seqByContact.get(c.id)).length },
        { value: 'none', label: 'No sequence',  count: allContacts.filter(c => !seqByContact.get(c.id)).length },
      ],
    },
    {
      key: 'email', label: 'Email',
      options: [
        { value: 'approved', label: 'Approved', count: allContacts.filter(c => { const ch = channelByKey.get(c.id + ':email'); return ch && ch.approval_status === 'approved'; }).length },
        { value: 'pending',  label: 'Pending',  count: allContacts.filter(c => { const ch = channelByKey.get(c.id + ':email'); return ch && ch.approval_status === 'pending'; }).length },
        { value: 'none',     label: 'None',     count: allContacts.filter(c => !channelByKey.get(c.id + ':email')).length },
      ],
    },
    {
      key: 'linkedin', label: 'LinkedIn',
      options: [
        { value: 'approved', label: 'Approved', count: allContacts.filter(c => { const ch = channelByKey.get(c.id + ':linkedin'); return ch && ch.approval_status === 'approved'; }).length },
        { value: 'pending',  label: 'Pending',  count: allContacts.filter(c => { const ch = channelByKey.get(c.id + ':linkedin'); return ch && ch.approval_status === 'pending'; }).length },
        { value: 'none',     label: 'None',     count: allContacts.filter(c => !channelByKey.get(c.id + ':linkedin')).length },
      ],
    },
    {
      key: 'in_batch', label: 'In batch',
      options: [
        { value: 'linkedin', label: 'In LinkedIn batch', count: allContacts.filter(c => membershipByContact.get(c.id)?.in_linkedin_batch).length },
        { value: 'email',    label: 'In Email batch',    count: allContacts.filter(c => membershipByContact.get(c.id)?.in_email_batch).length },
        { value: 'none',     label: 'Not in any batch',  count: allContacts.filter(c => { const m = membershipByContact.get(c.id); return !m?.in_linkedin_batch && !m?.in_email_batch; }).length },
      ],
    },
    {
      key: 'enriched', label: 'Enriched',
      options: [
        { value: 'yes', label: 'Enriched',     count: allContacts.filter(c => c.enriched_at).length },
        { value: 'no',  label: 'Not enriched', count: allContacts.filter(c => !c.enriched_at).length },
      ],
    },
    {
      key: 'has_linkedin_url', label: 'LinkedIn URL',
      options: [
        { value: 'yes', label: 'Has URL',     count: allContacts.filter(c => getContactLinkedinUrl(c)).length },
        { value: 'no',  label: 'Missing URL', count: allContacts.filter(c => !getContactLinkedinUrl(c)).length },
      ],
    },
    {
      key: 'has_email', label: 'Email address',
      options: [
        { value: 'yes', label: 'Has email',     count: allContacts.filter(c => c.email).length },
        { value: 'no',  label: 'Missing email', count: allContacts.filter(c => !c.email).length },
      ],
    },
  ];

  // Contact search debounce
  useEffect(() => {
    if (contactSearch.trim().length < 2) { setContactSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setContactSearching(true);
      try {
        const res = await fetch(`${API_BASE}/clients/${id}/contacts/search?q=${encodeURIComponent(contactSearch)}`);
        setContactSearchResults(await res.json());
      } catch (e) {}
      setContactSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [contactSearch, id]);

  async function fetchClient() { try { const res = await fetch(`${API_BASE}/clients/${id}`); setClient(await res.json()); } catch (e) {} }
  async function fetchCompanies() { try { const res = await fetch(`${API_BASE}/clients/${id}/companies`); const data = await res.json(); setCompanies(data); setFilteredCompanies(data); } catch (e) {} }

  async function fetchSweepStatus() {
    try {
      const res = await fetch(`${API_BASE}/clients/${id}/sweep-status`);
      const data = await res.json();
      const map = {};
      (data.companies || []).forEach(c => { map[c.id] = c; });
      setSweepMeta(map);
    } catch (e) {}
  }

  async function handleDetectSignals(company) {
    setDetectingSignals(company.id);
    const toastId = toast.loading('Scanning ' + company.name + ' for signals...');
    try {
      const res = await creditAwareFetch(`${API_BASE}/companies/${company.id}/detect-signals`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }
      });
      if (!res) { toast.dismiss(toastId); setDetectingSignals(null); return; }
      const data = await res.json();
      if (data.sweep_meta) {
        const meta = data.sweep_meta;
        const status = meta.next_sweep_due && new Date(meta.next_sweep_due) > new Date() ? 'fresh' : 'due';
        setSweepMeta(prev => ({ ...prev, [company.id]: { ...prev[company.id], ...meta, sweep_status: status } }));
      }
      const signalCount = (data.signals && data.signals.length) || 0;
      if (signalCount > 0) {
        toast.success('Found ' + signalCount + ' signal' + (signalCount === 1 ? '' : 's') + ' for ' + company.name, { id: toastId });
      } else {
        toast.success('No new signals for ' + company.name, { id: toastId });
      }
    } catch (e) {
      toast.error('Scan failed: ' + e.message, { id: toastId });
    }
    setDetectingSignals(null);
  }

  const [bulkScanning, setBulkScanning] = useState(false);
  const [bulkScanProgress, setBulkScanProgress] = useState({ current: 0, total: 0 });

  async function handleBulkScan() {
    if (!selectedIds.size || bulkScanning) return;
    const ids = Array.from(selectedIds);
    setBulkScanning(true);
    setBulkScanProgress({ current: 0, total: ids.length });
    const toastId = toast.loading('Scanning ' + ids.length + ' companies for signals...');
    let scanned = 0, totalSignals = 0;
    for (let i = 0; i < ids.length; i++) {
      const companyId = ids[i];
      const company = companies.find(c => c.id === companyId);
      if (!company) continue;
      setBulkScanProgress({ current: i + 1, total: ids.length });
      try {
        const res = await creditAwareFetch(`${API_BASE}/companies/${companyId}/detect-signals`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }
        });
        if (!res) { toast.dismiss(toastId); setBulkScanning(false); setBulkScanProgress({ current: 0, total: 0 }); return; }
        const data = await res.json();
        if (data.sweep_meta) {
          const meta = data.sweep_meta;
          const status = meta.next_sweep_due && new Date(meta.next_sweep_due) > new Date() ? 'fresh' : 'due';
          setSweepMeta(prev => ({ ...prev, [companyId]: { ...prev[companyId], ...meta, sweep_status: status } }));
        }
        if (data.signals && data.signals.length) totalSignals += data.signals.length;
        scanned++;
      } catch (e) {}
      if (i < ids.length - 1) await new Promise(r => setTimeout(r, 1000));
    }
    toast.success('Scanned ' + scanned + ' companies, found ' + totalSignals + ' new signal' + (totalSignals === 1 ? '' : 's'), { id: toastId });
    setBulkScanning(false);
    setBulkScanProgress({ current: 0, total: 0 });
    setSelectedIds(new Set());
  }
  async function fetchContacts(companyId) {
    try {
      const res = await fetch(`${API_BASE}/companies/${companyId}/contacts`);
      const data = await res.json();
      setContacts(data);
      const enriched = {};
      data.forEach(c => { if (c.enrichment_data) enriched[c.id] = typeof c.enrichment_data === 'string' ? JSON.parse(c.enrichment_data) : c.enrichment_data; });
      setEnrichedContacts(enriched);
    } catch (e) {}
  }

  async function handleSaveCompany() {
    if (!companyForm.name.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/clients/${id}/companies`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(companyForm) });
      if (r.status === 409) {
        const err = await r.json();
        toast.error(err.message || 'A company with this website or name already exists.', { duration: 5000 });
        setSaving(false);
        return;
      }
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.error || 'Failed to save company.');
        setSaving(false);
        return;
      }
      await fetchCompanies();
      setShowCompanyForm(false);
      setCompanyForm({ name: '', website: '', sector: '', size: '', location: '', linkedin_url: '', revenue: '' });
      setNameCheck({ checking: false, canonical: null });
    } catch (e) { setError('Failed to save company.'); }
    setSaving(false);
  }

  async function handleSaveContact() {
    if (!contactForm.full_name.trim() || !selectedCompany) return;
    setSaving(true);
    try {
      const __limRes = await creditAwareFetch(`${API_BASE}/companies/${selectedCompany.id}/contacts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...contactForm, client_id: id }) });
      if (!__limRes) { setSaving(false); return; } // 402 limit_reached — modal handled by creditAwareFetch
      const __body = await __limRes.json().catch(() => ({}));
      if (__body && (__body.error === 'duplicate' || __limRes.status === 409)) {
        toast.error(__body.message || 'A contact with this email or LinkedIn URL already exists.', { duration: 5000 });
        setSaving(false);
        return;
      }
      if (!__limRes.ok) {
        toast.error(__body.error || 'Failed to save contact.');
        setSaving(false);
        return;
      }
      await fetchContacts(selectedCompany.id);
      setShowContactForm(false);
      setContactForm({ full_name: '', job_title: '', email: '', linkedin_url: '', phone: '', notes: '' });
      toast.success('Contact added');
    } catch (e) { toast.error('Failed to save contact: ' + e.message); }
    setSaving(false);
  }

  async function handleEnrich(contact, silent = false) {
    setEnriching(contact.id);
    const toastId = silent ? null : toast.loading('Enriching ' + (contact.full_name || 'contact') + '...');
    try {
      const res = await creditAwareFetch(`${API_BASE}/contacts/${contact.id}/enrich`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      const data = await res.json();
      if (data.success && data.enrichment) {
        setEnrichedContacts(prev => ({ ...prev, [contact.id]: data.enrichment }));
        setContacts(prev => prev.map(c => c.id === contact.id ? { ...c, enrichment_data: data.enrichment, enriched_at: new Date().toISOString() } : c));
        if (!silent) toast.success('Enriched ' + (contact.full_name || 'contact'), { id: toastId });
      } else if (!silent) {
        toast.error('Could not enrich contact', { id: toastId });
      }
    } catch (e) {
      if (!silent) toast.error('Enrichment failed: ' + e.message, { id: toastId });
    }
    setEnriching(null);
  }

  async function handleBulkEnrich() {
    if (!selectedCompany || bulkEnriching) return;
    const unenriched = contacts.filter(c => !enrichedContacts[c.id]).slice(0, ENRICH_LIMIT);
    if (unenriched.length === 0) return;
    setBulkEnriching(true);
    setBulkEnrichProgress({ current: 0, total: unenriched.length });
    const toastId = toast.loading('Enriching ' + unenriched.length + ' contact' + (unenriched.length === 1 ? '' : 's') + '...');
    for (let i = 0; i < unenriched.length; i++) {
      await handleEnrich(unenriched[i], true);
      setBulkEnrichProgress({ current: i + 1, total: unenriched.length });
    }
    toast.success('Enriched ' + unenriched.length + ' contact' + (unenriched.length === 1 ? '' : 's'), { id: toastId });
    setBulkEnriching(false);
    setBulkEnrichProgress(null);
  }

  async function handleEmailBlur(email) {
    if (!email || !email.includes('@')) return;
    const domain = email.split('@')[1];
    if (!domain) return;
    setAddContactLookingUp(true);
    try {
      // First check if company already exists in workspace
      const existingRes = await creditAwareFetch(`${API_BASE}/clients/${id}/find-or-create-company`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, _checkOnly: true })
      });
      const existingData = await existingRes.json();
      if (existingData.company && !existingData.created) {
        setAddContactCompany(existingData.company);
        setAddContactForm(prev => ({ ...prev, company_name: existingData.company.name }));
        setAddContactLookingUp(false);
        return;
      }

      // Not found — look up via Claude
      const lookupRes = await fetch(`${API_BASE}/lookup-company-domain`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain })
      });
      const lookupData = await lookupRes.json();
      if (lookupData.name) {
        setAddContactForm(prev => ({
          ...prev,
          company_name: lookupData.name,
          _companyLookup: lookupData
        }));
      }
    } catch (e) {}
    setAddContactLookingUp(false);
  }

  async function handleSaveNewContact() {
    if (!addContactForm.full_name.trim()) return;
    setAddContactSaving(true);
    try {
      let company = addContactCompany;
      if (!company) {
        const lookup = addContactForm._companyLookup;
        const cRes = await fetch(`${API_BASE}/clients/${id}/companies`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: addContactForm.company_name,
            website: addContactForm.email ? `https://${addContactForm.email.split('@')[1]}` : '',
            sector: lookup?.sector || '',
            size: lookup?.size || '',
            location: lookup?.location || '',
          })
        });
        const cBody = await cRes.json().catch(() => ({}));
        if (cBody && cBody.error === 'duplicate' && cBody.existing_id) {
          // Company already exists — reuse it
          company = { id: cBody.existing_id, name: cBody.existing_name };
        } else if (!cRes.ok) {
          toast.error(cBody.message || cBody.error || 'Failed to create company.');
          setAddContactSaving(false);
          return;
        } else {
          company = cBody;
        }
      }
      if (!company || !company.id) throw new Error('Could not find or create company');
      const ctRes = await fetch(`${API_BASE}/companies/${company.id}/contacts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...addContactForm, client_id: id })
      });
      const ctBody = await ctRes.json().catch(() => ({}));
      if (ctBody && ctBody.error === 'duplicate') {
        toast.error(ctBody.message || 'A contact with this email or LinkedIn URL already exists.', { duration: 5000 });
        setAddContactSaving(false);
        return;
      }
      if (!ctRes.ok) {
        toast.error(ctBody.error || 'Failed to save contact.');
        setAddContactSaving(false);
        return;
      }
      setShowAddContact(false);
      setAddContactForm({ full_name: '', job_title: '', email: '', linkedin_url: '', phone: '', company_name: '' });
      setAddContactCompany(null);
      await fetchCompanies();
      const res = await fetch(`${API_BASE}/clients/${id}/contacts?limit=500`);
      setAllContacts(await res.json());
      toast.success('Contact added');
    } catch (e) { toast.error('Failed to save contact: ' + e.message); }
    setAddContactSaving(false);
  }

  function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.name.endsWith('.csv')) {
      Papa.parse(file, { header: true, skipEmptyLines: true, complete: r => setImportRows(r.data.map(mapRow).filter(r => r.company_name || r.full_name)) });
    } else {
      const reader = new FileReader();
      reader.onload = ev => {
        const wb = XLSX.read(ev.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
        setImportRows(data.map(mapRow).filter(r => r.company_name || r.full_name));
      };
      reader.readAsBinaryString(file);
    }
  }

  async function handleImport() {
    // Dry run first — preview what would happen
    setImporting(true);
    try {
      const res = await creditAwareFetch(`${API_BASE}/clients/${id}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: importRows, dry_run: true })
      });
      if (!res) return; // 402 limit_reached
      if (!res.ok) throw new Error(await res.text());
      const preview = await res.json();
      setImportPreview(preview);
      setImportStrategy('skip');
    } catch (e) { setError('Import preview failed: ' + e.message); }
    setImporting(false);
  }

  async function runImport(strategy) {
    setImporting(true);
    try {
      const res = await creditAwareFetch(`${API_BASE}/clients/${id}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: importRows, dedup_strategy: strategy })
      });
      if (!res) return;
      if (!res.ok) throw new Error(await res.text());
      setImportResult(await res.json());
      await fetchCompanies();
      setImportRows([]);
      setImportPreview(null);
    } catch (e) { setError('Import failed: ' + e.message); }
    setImporting(false);
  }

  async function handleBulkDeleteCompanies() {
    if (!selectedIds.size) return;
    if (!window.confirm(`Delete ${selectedIds.size} companies and all their contacts? This cannot be undone.`)) return;
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/companies/bulk`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(id), company_ids: Array.from(selectedIds), user_email: user?.primaryEmailAddress?.emailAddress })
      });
      setSelectedIds(new Set());
      setSelectedCompany(null);
      setContacts([]);
      await fetchCompanies();
    } catch (e) { setError('Bulk delete failed.'); }
  }

  async function loadAvailableBatches(channel) {
    try {
      const ep = channel === 'email' ? 'email-batches' : 'linkedin-batches';
      const res = await fetch(`${API_BASE}/clients/${id}/${ep}`);
      if (!res.ok) { setAvailableBatches([]); return; }
      const data = await res.json();
      setAvailableBatches(Array.isArray(data) ? data.filter(b => b.status !== 'exported') : []);
    } catch (e) {
      setAvailableBatches([]);
    }
  }

  function openAddToBatch() {
    if (!selectedGlobalContactIds.size) return;
    setAddToBatchOpen(true);
    setCreatingNewBatch(false);
    setSelectedBatchId('');
    setNewBatchName('');
    loadAvailableBatches(addToBatchChannel);
  }

  function closeAddToBatch() {
    setAddToBatchOpen(false);
    setBatchSubmitting(false);
  }

  function changeBatchChannel(channel) {
    setAddToBatchChannel(channel);
    setSelectedBatchId('');
    loadAvailableBatches(channel);
  }

  async function submitAddToBatch() {
    if (!selectedGlobalContactIds.size) return;
    setBatchSubmitting(true);
    try {
      // Step 1: figure out which contacts have an APPROVED channel sequence
      const channel = addToBatchChannel;
      const channelsRes = await fetch(`${API_BASE}/clients/${id}/sequence-channels?channel=${channel}&approval_status=approved`);
      const approvedChannels = channelsRes.ok ? await channelsRes.json() : [];
      const approvedById = new Map();
      for (const ch of approvedChannels) approvedById.set(ch.contact_id, ch);

      const selectedIds = Array.from(selectedGlobalContactIds);
      const eligible = selectedIds.filter(cid => approvedById.has(cid));
      const skipped = selectedIds.length - eligible.length;

      if (eligible.length === 0) {
        toast.error(`No eligible contacts. ${skipped} skipped — need approved ${channel} sequences.`);
        setBatchSubmitting(false);
        return;
      }

      // Step 2: create batch if needed
      let batchId = selectedBatchId;
      if (creatingNewBatch) {
        if (!newBatchName.trim()) { toast.error('Batch name required'); setBatchSubmitting(false); return; }
        const ep = channel === 'email' ? 'email-batches' : 'linkedin-batches';
        const body = channel === 'email'
          ? { name: newBatchName.trim(), destination_type: newBatchDestination }
          : { name: newBatchName.trim() };
        const createRes = await fetch(`${API_BASE}/clients/${id}/${ep}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!createRes.ok) { toast.error('Failed to create batch'); setBatchSubmitting(false); return; }
        const created = await createRes.json();
        batchId = created.id;
      }

      if (!batchId) { toast.error('Pick a batch or create a new one'); setBatchSubmitting(false); return; }

      // Step 3: add eligible contacts
      const ep = channel === 'email' ? 'email-batches' : 'linkedin-batches';
      let added = 0, failed = 0;
      for (const cid of eligible) {
        const ch = approvedById.get(cid);
        try {
          const res = await fetch(`${API_BASE}/${ep}/${batchId}/contacts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contact_id: cid, sequence_id: ch.sequence_id }),
          });
          if (res.ok) added++; else failed++;
        } catch (e) { failed++; }
      }

      // Step 4: report
      const parts = [`Added ${added}`];
      if (failed > 0) parts.push(`${failed} failed`);
      if (skipped > 0) parts.push(`${skipped} skipped (no approved ${channel})`);
      toast.success(parts.join(' · '));

      setSelectedGlobalContactIds(new Set());
      closeAddToBatch();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally {
      setBatchSubmitting(false);
    }
  }

  async function handleBulkDeleteGlobalContacts() {
    if (!selectedGlobalContactIds.size) return;
    if (!window.confirm(`Delete ${selectedGlobalContactIds.size} contacts? This cannot be undone.`)) return;
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/contacts/bulk`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(id), contact_ids: Array.from(selectedGlobalContactIds), user_email: user?.primaryEmailAddress?.emailAddress })
      });
      setSelectedGlobalContactIds(new Set());
      const res = await fetch(`${API_BASE}/clients/${id}/contacts?limit=500`);
      setAllContacts(await res.json());
    } catch (e) { setError('Bulk delete failed.'); }
  }

  async function handleBulkDeleteContacts() {
    if (!selectedContactIds.size) return;
    if (!window.confirm(`Delete ${selectedContactIds.size} contacts? This cannot be undone.`)) return;
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/contacts/bulk`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(id), contact_ids: Array.from(selectedContactIds), user_email: user?.primaryEmailAddress?.emailAddress })
      });
      setSelectedContactIds(new Set());
      if (selectedCompany) await fetchContacts(selectedCompany.id);
      const res = await fetch(`${API_BASE}/clients/${id}/contacts?limit=500`);
      setAllContacts(await res.json());
    } catch (e) { setError('Bulk delete failed.'); }
  }

  async function handleDeleteContact(contactId, companyId) {
    if (!window.confirm('Delete this contact? This cannot be undone.')) return;
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ user_email: user?.primaryEmailAddress?.emailAddress })
      });
      if (!r.ok) { setError('Delete failed (' + r.status + ').'); return; }
      await fetchContacts(companyId);
      const res = await fetch(`${API_BASE}/clients/${id}/contacts?limit=500`);
      setAllContacts(await res.json());
    } catch (e) { setError('Delete failed.'); }
  }

  async function handleDeleteCompany(companyId) {
    if (!window.confirm('Delete this company and all its contacts? This cannot be undone.')) return;
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/companies/${companyId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ user_email: user?.primaryEmailAddress?.emailAddress })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.error || 'Delete failed (status ' + r.status + ')');
        return;
      }
      setSelectedCompany(null);
      setContacts([]);
      await fetchCompanies();
      toast.success('Company deleted');
    } catch (e) { toast.error('Delete failed: ' + e.message); }
  }

  function handleSequence(contactId, clientId) {
    setSelectedContact(null);
    const toastId = toast.loading('Generating sequence — this can take up to 60 seconds...', { duration: 90000 });
    navigate(`/clients/${clientId}/sequences?contactId=${contactId}&generating=true&from=prospects`);
    creditAwareFetch(`${API_BASE}/contacts/${contactId}/generate-sequence`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contact_id: contactId, client_id: clientId })
    }).then(r => {
      if (!r) { toast.dismiss(toastId); return null; } // 402 — modal handled
      if (r.status === 499) { toast('Sequence generation stopped', { id: toastId, icon: '⏹️' }); return null; }
      if (!r.ok) { toast.error('Sequence generation failed', { id: toastId }); return null; }
      return r.json();
    }).then(seq => {
      if (!seq) return;
      return fetch(`${API_BASE}/contacts/${contactId}/sequences`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, email_sequence: seq.email_sequence, linkedin_sequence: seq.linkedin_sequence, call_sequence: seq.call_sequence })
      }).then(() => {
        toast.success('Sequence generated', { id: toastId });
        try { window.dispatchEvent(new CustomEvent('ce:sequenceGenerated', { detail: { contactId, clientId } })); } catch {}
      });
    }).catch(e => { console.error(e); toast.error('Sequence generation failed', { id: toastId }); });
  }

  const pagedCompanies = cfActive ? (cfRows || []) : filteredCompanies.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div style={{ backgroundColor: '#FAFAF8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      <div style={{ padding: '1.5rem 2rem', maxWidth: '1400px', margin: '0 auto', boxSizing: 'border-box' }}>

        <button onClick={() => navigate('/clients/' + id)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.83rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem' }}>← {client?.name || 'Back'}</button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: '500', margin: '0 0 0.25rem', color: '#1A1A1A' }}>Prospects</h1>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>{client?.name}</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={() => navigate('/clients/' + id + '/find-similar')} style={{ backgroundColor: 'transparent', color: '#8B1A1A', border: '1px solid #8B1A1A', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer' }}>Find similar companies →</button>
            <button onClick={() => navigate('/clients/' + id + '/find-similar-contacts')} style={{ backgroundColor: 'transparent', color: '#8B1A1A', border: '1px solid #8B1A1A', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer' }}>Find similar contacts →</button>
            <button onClick={() => setShowImport(!showImport)} style={{ backgroundColor: 'transparent', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer' }}>Import CSV / Excel</button>
            <button onClick={() => setShowAddContact(true)} style={{ backgroundColor: 'transparent', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer' }}>+ Add Contact</button>
            <button onClick={() => setShowCompanyForm(true)} style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '500' }}>+ Add Company</button>
          </div>
        </div>

        {error && <p style={{ color: '#C53030', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</p>}

        {/* Add Contact Modal */}
        {showAddContact && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '1.5rem', width: '520px', maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.16)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#1A1A1A' }}>Add contact</h3>
                <button onClick={() => setShowAddContact(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: '#888', cursor: 'pointer' }}>×</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div><label style={lbl}>Full name *</label><input value={addContactForm.full_name} onChange={e => setAddContactForm({...addContactForm, full_name: e.target.value})} placeholder="e.g. Sarah Johnson" style={inp} /></div>
                <div><label style={lbl}>Job title</label><input value={addContactForm.job_title} onChange={e => setAddContactForm({...addContactForm, job_title: e.target.value})} placeholder="e.g. Head of Finance" style={inp} /></div>
                <div>
                  <label style={lbl}>Email — we'll use the domain to find their company</label>
                  <input value={addContactForm.email} onChange={e => setAddContactForm({...addContactForm, email: e.target.value})} onBlur={e => handleEmailBlur(e.target.value)} placeholder="e.g. sarah@company.com" style={inp} />
                </div>
                {addContactLookingUp && <p style={{ margin: 0, fontSize: '0.78rem', color: '#888' }}>Looking up company from email domain...</p>}
                {addContactCompany && <div style={{ backgroundColor: '#F0FFF4', border: '0.5px solid #276749', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#276749' }}>Existing company matched: <strong>{addContactCompany.name}</strong></p>
                </div>}
                {!addContactCompany && addContactForm.company_name && !addContactLookingUp && <div style={{ backgroundColor: '#EBF8FF', border: '0.5px solid #2B6CB0', borderRadius: '6px', padding: '0.5rem 0.75rem', marginBottom: '0.25rem' }}>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#2B6CB0' }}>Company found: <strong>{addContactForm.company_name}</strong>{addContactForm._companyLookup?.sector ? ' · ' + addContactForm._companyLookup.sector : ''}{addContactForm._companyLookup?.location ? ' · ' + addContactForm._companyLookup.location : ''}</p>
                </div>}
                <div><label style={lbl}>Company name</label><input value={addContactForm.company_name} onChange={e => setAddContactForm({...addContactForm, company_name: e.target.value})} placeholder="e.g. Acme Ltd" style={inp} /></div>
                <div><label style={lbl}>LinkedIn URL</label><input value={addContactForm.linkedin_url} onChange={e => setAddContactForm({...addContactForm, linkedin_url: e.target.value})} placeholder="linkedin.com/in/sarah" style={inp} /></div>
                <div><label style={lbl}>Phone</label><input value={addContactForm.phone} onChange={e => setAddContactForm({...addContactForm, phone: e.target.value})} placeholder="+44 7700 900000" style={inp} /></div>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                <button onClick={handleSaveNewContact} disabled={addContactSaving || !addContactForm.full_name.trim()} style={{ flex: 1, backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 1rem', fontSize: '0.875rem', cursor: 'pointer', opacity: addContactSaving || !addContactForm.full_name.trim() ? 0.5 : 1 }}>{addContactSaving ? 'Saving...' : 'Save contact'}</button>
                <button onClick={() => { setShowAddContact(false); setAddContactCompany(null); setAddContactForm({ full_name: '', job_title: '', email: '', linkedin_url: '', phone: '', company_name: '' }); }} style={{ backgroundColor: 'transparent', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.6rem 1rem', fontSize: '0.875rem', cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Import panel */}
        {showImport && (
          <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #276749', borderRadius: '10px', padding: '1.5rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: '500', marginTop: 0, marginBottom: '0.5rem' }}>Import from CSV or Excel</h2>
            <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>Supports Apollo exports. Columns mapped automatically.</p>
            <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} style={{ fontSize: '0.85rem', color: '#4A5568', marginBottom: '1rem' }} />
            {importRows.length > 0 && (
              <>
                <p style={{ fontSize: '0.85rem', color: '#4A5568', marginBottom: '0.75rem' }}>{importRows.length} rows parsed.</p>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button onClick={handleImport} disabled={importing} style={{ backgroundColor: '#276749', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer', opacity: importing ? 0.5 : 1 }}>
                    {importing ? 'Checking...' : `Preview import (${importRows.length} rows)`}
                  </button>
                  <button onClick={() => { setImportRows([]); setShowImport(false); setImportPreview(null); setImportResult(null); }} style={{ backgroundColor: 'transparent', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer' }}>Cancel</button>
                </div>
              </>
            )}
            {importResult && (
              <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#F0FFF4', border: '1px solid #C6F6D5', borderRadius: '6px', fontSize: '0.85rem', color: '#276749' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Import complete</div>
                <div>Companies: {importResult.companiesCreated} created, {importResult.companiesSkipped || 0} skipped</div>
                <div>Contacts: {importResult.contactsCreated} created, {importResult.contactsUpdated || 0} updated, {importResult.contactsSkipped || 0} skipped</div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div style={{ marginTop: '0.4rem', color: '#C53030' }}>{importResult.errors.length} row error(s) — check console</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Add company form */}
        {showCompanyForm && (
          <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #8B1A1A', borderRadius: '10px', padding: '1.5rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: '500', marginTop: 0, marginBottom: '1.5rem' }}>Add company</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
              <div><label style={lbl}>Company name *</label><input value={companyForm.name} onChange={e => setCompanyForm({...companyForm, name: e.target.value})} placeholder="e.g. Acme Ltd" style={inp} /></div>
              <div><label style={lbl}>Website</label><input value={companyForm.website} onChange={e => setCompanyForm({...companyForm, website: e.target.value})} onBlur={e => checkCompanyName(e.target.value)} placeholder="e.g. acme.co.uk" style={inp} /></div>
              <div><label style={lbl}>Sector</label><select value={companyForm.sector} onChange={e => setCompanyForm({...companyForm, sector: e.target.value})} style={inp}><option value="">Select sector</option>{SECTORS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              <div><label style={lbl}>Size</label><input value={companyForm.size} onChange={e => setCompanyForm({...companyForm, size: e.target.value})} placeholder="e.g. 10-50" style={inp} /></div>
              <div><label style={lbl}>Country</label><input value={companyForm.location} onChange={e => setCompanyForm({...companyForm, location: e.target.value})} placeholder="e.g. United Kingdom" style={inp} /></div>
              <div><label style={lbl}>LinkedIn URL</label><input value={companyForm.linkedin_url} onChange={e => setCompanyForm({...companyForm, linkedin_url: e.target.value})} placeholder="linkedin.com/company/acme" style={inp} /></div>
            </div>
            {nameCheck.canonical && nameCheck.canonical.toLowerCase().trim() !== (companyForm.name || '').toLowerCase().trim() && (
              <div style={{ backgroundColor: '#FFF8E1', border: '0.5px solid #C9A227', borderRadius: '6px', padding: '0.6rem 0.85rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#5D4A1A', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                <span>This site refers to itself as <strong>{nameCheck.canonical}</strong>. Use that name?</span>
                <button onClick={() => { setCompanyForm({...companyForm, name: nameCheck.canonical}); setNameCheck({ checking: false, canonical: null }); }} style={{ backgroundColor: '#C9A227', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.35rem 0.7rem', fontSize: '0.78rem', cursor: 'pointer', fontWeight: '500' }}>Use this name</button>
              </div>
            )}
            {nameCheck.checking && <p style={{ margin: '0 0 0.75rem', fontSize: '0.78rem', color: '#888' }}>Checking site name...</p>}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={handleSaveCompany} disabled={saving || !companyForm.name.trim()} style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer', opacity: saving || !companyForm.name.trim() ? 0.5 : 1 }}>{saving ? 'Saving...' : 'Save company'}</button>
              <button onClick={() => setShowCompanyForm(false)} style={{ backgroundColor: 'transparent', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Search mode toggle + search bar */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
          <div style={{ display: 'flex', backgroundColor: '#fff', border: '1px solid #D4CFC9', borderRadius: '6px', overflow: 'hidden' }}>
            <button onClick={() => { setSearchMode('company'); setContactSearch(''); setContactSearchResults([]); }}
              style={{ padding: '0.5rem 1rem', fontSize: '0.82rem', border: 'none', background: searchMode === 'company' ? '#8B1A1A' : 'transparent', color: searchMode === 'company' ? '#fff' : '#888', cursor: 'pointer' }}>
              Companies
            </button>
            <button onClick={() => { setSearchMode('contact'); setSearch(''); }}
              style={{ padding: '0.5rem 1rem', fontSize: '0.82rem', border: 'none', background: searchMode === 'contact' ? '#8B1A1A' : 'transparent', color: searchMode === 'contact' ? '#fff' : '#888', cursor: 'pointer' }}>
              Contacts
            </button>
          </div>
          {searchMode === 'company' ? (
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by company name, sector, or country..."
              style={{ ...inp, maxWidth: '400px' }} />
          ) : (
            <input type="text" value={contactSearch} onChange={e => setContactSearch(e.target.value)} placeholder="Search contacts by name, title, email, or company..."
              style={{ ...inp, maxWidth: '400px' }} />
          )}
          {contactSearching && <span style={{ fontSize: '0.78rem', color: '#888' }}>Searching...</span>}
        </div>

        {/* Contact search results */}
        {searchMode === 'contact' && contactSearch.length < 2 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontSize: '0.75rem', color: '#888', margin: '0 0 0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '500' }}>
              {allContactsLoading ? 'Loading contacts...' : `${allContacts.length} contacts`}
            </p>
            {allContactsLoading ? (
              <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '2rem', textAlign: 'center' }}>
                <p style={{ margin: 0, color: '#888', fontSize: '0.85rem' }}>Loading...</p>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
                <FilterRail
                  schema={CONTACT_SCHEMA}
                  value={sfTree}
                  onChange={(t) => { setSfTree(t); setSfPage(0); }}
                  matchCount={sfActive ? sfTotal : undefined}
                  loading={sfLoading}
                  savedViews={savedViews}
                  onSaveView={saveCurrentView}
                  onApplyView={(v) => { setSfTree(v.tree); setSfPage(0); }}
                  onDeleteView={deleteView}
                  targets={filterTargets}
                  distincts={filterDistincts}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                {selectedGlobalContactIds.size > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', backgroundColor: '#FFF5F5', border: '0.5px solid #FEB2B2', borderRadius: '8px', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: '500', color: '#C53030' }}>{selectedGlobalContactIds.size} selected</span>
                    <button onClick={selectAllContacts} style={{ fontSize: '0.72rem', color: '#1A56A0', background: 'none', border: '1px solid #BFD7F5', borderRadius: '20px', padding: '0.15rem 0.5rem', cursor: 'pointer' }}>Select all {sfActive ? sfTotal : allContacts.length}</button>
                    <button onClick={() => setSelectedGlobalContactIds(new Set())} style={{ fontSize: '0.72rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '20px', padding: '0.15rem 0.5rem', cursor: 'pointer' }}>Deselect all</button>
                    <button onClick={openAddToBatch} style={{ fontSize: '0.72rem', color: '#fff', background: '#1A56A0', border: 'none', borderRadius: '20px', padding: '0.15rem 0.6rem', cursor: 'pointer', fontWeight: '500' }}>Add to batch</button>
                    <button onClick={handleBulkDeleteGlobalContacts} style={{ fontSize: '0.72rem', color: '#fff', background: '#C53030', border: 'none', borderRadius: '20px', padding: '0.15rem 0.6rem', cursor: 'pointer', fontWeight: '500' }}>Delete {selectedGlobalContactIds.size}</button>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {(sfActive ? (sfRows || []) : filteredContacts.slice(contactPage * PAGE_SIZE, (contactPage + 1) * PAGE_SIZE)).map(ct => {
                    const score = ct.score;
                    const scoreColour = score >= 8 ? '#276749' : score >= 6 ? '#B7791F' : '#888';
                    const scoreBg = score >= 8 ? '#F0FFF4' : score >= 6 ? '#FFFBEB' : '#F7F7F7';
                    return (
                      <div key={ct.id}
                        style={{ background: selectedGlobalContactIds.has(ct.id) ? '#FFF5F5' : '#fff', border: '0.5px solid #E8E4E0', borderLeft: `3px solid ${selectedGlobalContactIds.has(ct.id) ? '#C53030' : '#8B1A1A'}`, borderRadius: '10px', padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                        <input type="checkbox" checked={selectedGlobalContactIds.has(ct.id)} onChange={e => { const n = new Set(selectedGlobalContactIds); if (e.target.checked) n.add(ct.id); else n.delete(ct.id); setSelectedGlobalContactIds(n); }} style={{ flexShrink: 0 }} />
                        <div onClick={() => setSelectedContact(ct.id)} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                            <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '500', color: '#1A1A1A' }}>{ct.full_name}</p>
                            {score && <span style={{ fontSize: '0.72rem', fontWeight: '600', color: scoreColour, backgroundColor: scoreBg, padding: '0.15rem 0.4rem', borderRadius: '20px', flexShrink: 0 }}>{score}/10</span>}
                          </div>
                          <p style={{ margin: 0, fontSize: '0.78rem', color: '#888' }}>{ct.job_title} · <span style={{ color: '#8B1A1A' }}>{ct.company_name}</span></p>
                          {ct.email && <p style={{ margin: '0.1rem 0 0', fontSize: '0.72rem', color: '#AAA' }}>{ct.email}</p>}
                        </div>
                        <span onClick={() => setSelectedContact(ct.id)} style={{ fontSize: '0.75rem', color: '#8B1A1A', flexShrink: 0, cursor: 'pointer' }}>View →</span>
                      </div>
                    );
                  })}
                </div>
                {(sfActive ? sfTotal > SF_PAGE_SIZE : filteredContacts.length > PAGE_SIZE) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0', marginTop: '0.5rem' }}>
                    {sfActive ? (
                      <span style={{ fontSize: '0.75rem', color: '#888' }}>{sfPage * SF_PAGE_SIZE + 1}–{Math.min((sfPage + 1) * SF_PAGE_SIZE, sfTotal)} of {sfTotal}</span>
                    ) : (
                      <span style={{ fontSize: '0.75rem', color: '#888' }}>{Math.min(contactPage * PAGE_SIZE + 1, filteredContacts.length)}–{Math.min((contactPage + 1) * PAGE_SIZE, filteredContacts.length)} of {filteredContacts.length}</span>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button onClick={() => sfActive ? setSfPage(p => Math.max(0, p - 1)) : setContactPage(p => Math.max(0, p - 1))} disabled={sfActive ? sfPage === 0 : contactPage === 0} style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '4px', color: (sfActive ? sfPage === 0 : contactPage === 0) ? '#CCC' : '#4A5568', padding: '0.2rem 0.6rem', fontSize: '0.75rem', cursor: (sfActive ? sfPage === 0 : contactPage === 0) ? 'default' : 'pointer' }}>Prev</button>
                      <button onClick={() => sfActive ? setSfPage(p => p + 1) : setContactPage(p => p + 1)} disabled={sfActive ? (sfPage + 1) * SF_PAGE_SIZE >= sfTotal : (contactPage + 1) * PAGE_SIZE >= filteredContacts.length} style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '4px', color: (sfActive ? (sfPage + 1) * SF_PAGE_SIZE >= sfTotal : (contactPage + 1) * PAGE_SIZE >= filteredContacts.length) ? '#CCC' : '#4A5568', padding: '0.2rem 0.6rem', fontSize: '0.75rem', cursor: (sfActive ? (sfPage + 1) * SF_PAGE_SIZE >= sfTotal : (contactPage + 1) * PAGE_SIZE >= filteredContacts.length) ? 'default' : 'pointer' }}>Next</button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            )}
          </div>
        )}

        {searchMode === 'contact' && (contactSearch.length >= 2) && (
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontSize: '0.75rem', color: '#888', margin: '0 0 0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '500' }}>
              {contactSearchResults.length} contact{contactSearchResults.length !== 1 ? 's' : ''} matching "{contactSearch}"
            </p>
            {contactSearchResults.length === 0 && !contactSearching ? (
              <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.5rem', textAlign: 'center' }}>
                <p style={{ margin: 0, color: '#888', fontSize: '0.85rem' }}>No contacts found.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {contactSearchResults.map(ct => {
                  const score = ct.score;
                  const scoreColour = score >= 8 ? '#276749' : score >= 6 ? '#B7791F' : '#888';
                  const scoreBg = score >= 8 ? '#F0FFF4' : score >= 6 ? '#FFFBEB' : '#F7F7F7';
                  return (
                    <div key={ct.id} onClick={() => setSelectedContact(ct.id)}
                      style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #8B1A1A', borderRadius: '10px', padding: '0.85rem 1.25rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                          <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '500', color: '#1A1A1A' }}>{ct.full_name}</p>
                          {score && <span style={{ fontSize: '0.72rem', fontWeight: '600', color: scoreColour, backgroundColor: scoreBg, padding: '0.15rem 0.4rem', borderRadius: '20px', flexShrink: 0 }}>{score}/10</span>}
                          {ct.sequence_status && <span style={{ fontSize: '0.68rem', color: ct.sequence_status === 'approved' ? '#276749' : '#B7791F', backgroundColor: ct.sequence_status === 'approved' ? '#F0FFF4' : '#FFFBEB', padding: '0.1rem 0.4rem', borderRadius: '20px', flexShrink: 0 }}>{ct.sequence_status}</span>}
                        </div>
                        <p style={{ margin: 0, fontSize: '0.78rem', color: '#888' }}>{ct.job_title} · <span style={{ color: '#8B1A1A' }}>{ct.company_name}</span></p>
                        {ct.email && <p style={{ margin: '0.1rem 0 0', fontSize: '0.72rem', color: '#AAA' }}>{ct.email}</p>}
                      </div>
                      <span style={{ fontSize: '0.75rem', color: '#8B1A1A', flexShrink: 0 }}>View →</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 1.25rem', backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #8B1A1A', borderRadius: '10px', marginBottom: '1rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1A1A1A' }}>{selectedIds.size} selected</span>
            <button onClick={selectAllCompanies} style={{ fontSize: '0.78rem', color: '#1A56A0', background: 'none', border: '1px solid #BFD7F5', borderRadius: '20px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>Select all {cfActive ? cfTotal : filteredCompanies.length}</button>
            <button onClick={() => setSelectedIds(new Set())} style={{ fontSize: '0.78rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '20px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>Deselect all</button>
            <button onClick={handleBulkScan} disabled={bulkScanning}
              style={{ fontSize: '0.78rem', color: '#fff', background: '#6B46C1', border: 'none', borderRadius: '20px', padding: '0.2rem 0.8rem', cursor: 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.4rem', opacity: bulkScanning ? 0.7 : 1 }}>
              {bulkScanning ? (
                <><span style={{ display: 'inline-block', width: '8px', height: '8px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Scanning {bulkScanProgress.current}/{bulkScanProgress.total}...</>
              ) : `Scan ${selectedIds.size} ${selectedIds.size === 1 ? 'company' : 'companies'} for signals`}
            </button>
            <button onClick={handleBulkDeleteCompanies} style={{ fontSize: '0.78rem', color: '#fff', background: '#C53030', border: 'none', borderRadius: '20px', padding: '0.2rem 0.8rem', cursor: 'pointer', fontWeight: '500' }}>Delete {selectedIds.size}</button>
          </div>
        )}

        {/* Split view — companies + contacts */}
        {searchMode === 'company' && (
          <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
          <FilterRail
            schema={COMPANY_SCHEMA}
            value={cfTree}
            onChange={(t) => { setCfTree(t); setCfPage(0); }}
            matchCount={cfActive ? cfTotal : undefined}
            loading={cfLoading}
            savedViews={companyViews}
            onSaveView={async (name, shared) => { try { const token = await getToken(); await fetch(`${API_BASE}/saved-filters/companies`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ client_id: parseInt(id), name, tree: cfTree, shared }) }); loadCompanyViews(); } catch (e) {} }}
            onApplyView={(v) => { setCfTree(v.tree); setCfPage(0); }}
            onDeleteView={async (vid) => { try { const token = await getToken(); await fetch(`${API_BASE}/saved-filters/${vid}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }); loadCompanyViews(); } catch (e) {} }}
          />
          <div style={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: '3fr 2fr', gap: '1.5rem', alignItems: 'flex-start' }}>

            {/* Companies */}
            <div>
              <p style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.75rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'}{search ? ` matching "${search}"` : ''}</p>
              {filteredCompanies.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#888', backgroundColor: '#fff', borderRadius: '10px', border: '0.5px solid #E8E4E0' }}>
                  <p style={{ margin: 0, fontWeight: '500' }}>{companies.length === 0 ? 'No companies yet.' : 'No results found.'}</p>
                </div>
              ) : (
                <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                      <thead>
                        <tr style={{ borderBottom: '0.5px solid #E8E4E0', backgroundColor: '#FAFAF8' }}>
                          <th style={{ padding: '0.6rem 1rem', width: '40px' }}>
                            <input type="checkbox" onChange={e => { if (e.target.checked) setSelectedIds(new Set(pagedCompanies.map(c => c.id))); else setSelectedIds(new Set()); }} checked={pagedCompanies.length > 0 && pagedCompanies.every(c => selectedIds.has(c.id))} />
                          </th>
                          {['Company','Sector','Location','Size','Signals',''].map(h => (
                            <th key={h} style={{ padding: '0.6rem 1rem', textAlign: 'left', color: '#888', fontWeight: '500', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pagedCompanies.map(company => (
                          <tr key={company.id} style={{ borderBottom: '0.5px solid #F0EDE9', backgroundColor: selectedIds.has(company.id) ? '#FFF5F5' : selectedCompany?.id === company.id ? '#F5F2EF' : 'transparent', cursor: 'pointer' }}>
                            <td style={{ padding: '0.6rem 1rem' }}>
                              <input type="checkbox" checked={selectedIds.has(company.id)} onChange={e => { e.stopPropagation(); const n = new Set(selectedIds); if (e.target.checked) n.add(company.id); else n.delete(company.id); setSelectedIds(n); }} onClick={e => e.stopPropagation()} />
                            </td>
                            <td onClick={() => setCompanyPanel(company)} style={{ padding: '0.6rem 1rem', fontWeight: '500', color: companyPanel?.id === company.id ? '#8B1A1A' : '#1A1A1A', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>{company.name}</td>
                            <td style={{ padding: '0.6rem 1rem', color: '#888', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{company.sector || ''}</td>
                            <td style={{ padding: '0.6rem 1rem', color: '#888' }}>{company.location || ''}</td>
                            <td style={{ padding: '0.6rem 1rem', color: '#888' }}>{company.size || ''}</td>
                            <td style={{ padding: '0.6rem 1rem', color: '#888', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{company.website || ''}</td>
                            <td style={{ padding: '0.6rem 1rem' }} onClick={e => e.stopPropagation()}>
                              {(() => {
                                const meta = sweepMeta[company.id];
                                const status = meta?.sweep_status || 'never';
                                const colours = { fresh: { bg: '#F0FFF4', color: '#276749', border: '#9AE6B4' }, due: { bg: '#FFFBEB', color: '#B7791F', border: '#F6E05E' }, never: { bg: '#F7F7F7', color: '#888', border: '#E2E8F0' } };
                                const c = colours[status] || colours.never;
                                return (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    {detectingSignals === company.id ? (
                                      <span style={{ fontSize: '0.68rem', fontWeight: '600', color: '#6B46C1', backgroundColor: '#F5F0FF', border: '1px solid #D4B8F5', borderRadius: '20px', padding: '0.15rem 0.6rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                        <span style={{ display: 'inline-block', width: '8px', height: '8px', border: '2px solid #6B46C1', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                                        Scanning...
                                      </span>
                                    ) : (
                                      <>
                                        <span title={status === 'fresh' ? 'Scanned recently — signals are up to date' : status === 'due' ? 'Scan overdue — click Scan to check for new signals' : 'Never scanned — click Scan to detect signals'}
                                          style={{ fontSize: '0.68rem', fontWeight: '600', color: c.color, backgroundColor: c.bg, border: '1px solid ' + c.border, borderRadius: '20px', padding: '0.15rem 0.5rem', whiteSpace: 'nowrap', cursor: 'help' }}>
                                          {status === 'fresh' ? '✓ Up to date' : status === 'due' ? '⚠ Needs scan' : '○ Not scanned'}
                                        </span>
                                        <button onClick={() => handleDetectSignals(company)}
                                          title="Scan for signals — funding rounds, leadership changes, hiring activity"
                                          style={{ fontSize: '0.68rem', color: '#6B46C1', background: 'none', border: '1px solid #D4B8F5', borderRadius: '20px', padding: '0.15rem 0.5rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                          Scan
                                        </button>
                                      </>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                          <td style={{ padding: '0.6rem 1rem' }}><button onClick={e => { e.stopPropagation(); handleDeleteCompany(company.id); }} style={{ fontSize: '0.72rem', color: '#C53030', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Delete</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 1rem', borderTop: '0.5px solid #E8E4E0' }}>
                    {cfActive ? (
                      <span style={{ fontSize: '0.75rem', color: '#888' }}>{cfTotal === 0 ? 0 : cfPage * SF_PAGE_SIZE + 1}–{Math.min((cfPage + 1) * SF_PAGE_SIZE, cfTotal)} of {cfTotal}</span>
                    ) : (
                      <span style={{ fontSize: '0.75rem', color: '#888' }}>{Math.min(page * PAGE_SIZE + 1, filteredCompanies.length)}–{Math.min((page + 1) * PAGE_SIZE, filteredCompanies.length)} of {filteredCompanies.length}</span>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button onClick={() => cfActive ? setCfPage(p => Math.max(0, p - 1)) : setPage(p => Math.max(0, p - 1))} disabled={cfActive ? cfPage === 0 : page === 0} style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '4px', color: (cfActive ? cfPage === 0 : page === 0) ? '#CCC' : '#4A5568', padding: '0.2rem 0.6rem', fontSize: '0.75rem', cursor: (cfActive ? cfPage === 0 : page === 0) ? 'default' : 'pointer' }}>Prev</button>
                      <button onClick={() => cfActive ? setCfPage(p => p + 1) : setPage(p => p + 1)} disabled={cfActive ? (cfPage + 1) * SF_PAGE_SIZE >= cfTotal : (page + 1) * PAGE_SIZE >= filteredCompanies.length} style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '4px', color: (cfActive ? (cfPage + 1) * SF_PAGE_SIZE >= cfTotal : (page + 1) * PAGE_SIZE >= filteredCompanies.length) ? '#CCC' : '#4A5568', padding: '0.2rem 0.6rem', fontSize: '0.75rem', cursor: (cfActive ? (cfPage + 1) * SF_PAGE_SIZE >= cfTotal : (page + 1) * PAGE_SIZE >= filteredCompanies.length) ? 'default' : 'pointer' }}>Next</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Contacts */}
            <div>
              {selectedCompany ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <p style={{ fontSize: '0.75rem', color: '#888', margin: 0, fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{selectedCompany.name} · {contacts.length} contacts</p>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button onClick={() => setShowContactForm(true)} style={{ backgroundColor: 'transparent', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '20px', padding: '0.2rem 0.6rem', fontSize: '0.78rem', cursor: 'pointer' }}>+ Add</button>
                      <button onClick={handleBulkEnrich} disabled={bulkEnriching || !contacts.length}
                        style={{ backgroundColor: 'transparent', color: '#6B46C1', border: '1px solid #D4B8F5', borderRadius: '20px', padding: '0.2rem 0.6rem', fontSize: '0.78rem', cursor: 'pointer', opacity: bulkEnriching || !contacts.length ? 0.5 : 1 }}>
                        {bulkEnriching ? `Enriching ${bulkEnrichProgress?.current}/${bulkEnrichProgress?.total}...` : `Enrich all`}
                      </button>
                    </div>
                  </div>

                  {showContactForm && (
                    <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <div><label style={lbl}>Full name *</label><input value={contactForm.full_name} onChange={e => setContactForm({...contactForm, full_name: e.target.value})} placeholder="e.g. Sarah Johnson" style={inp} /></div>
                        <div><label style={lbl}>Job title</label><input value={contactForm.job_title} onChange={e => setContactForm({...contactForm, job_title: e.target.value})} placeholder="e.g. Head of Finance" style={inp} /></div>
                        <div><label style={lbl}>Email</label><input type="email" value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} placeholder="e.g. sarah@company.com" style={inp} /></div>
                        <div><label style={lbl}>LinkedIn URL</label><input value={contactForm.linkedin_url} onChange={e => setContactForm({...contactForm, linkedin_url: e.target.value})} placeholder="linkedin.com/in/sarah" style={inp} /></div>
                        <div><label style={lbl}>Phone</label><input value={contactForm.phone} onChange={e => setContactForm({...contactForm, phone: e.target.value})} placeholder="+44 7700 900000" style={inp} /></div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                        <button onClick={handleSaveContact} disabled={saving || !contactForm.full_name.trim()} style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.875rem', cursor: 'pointer', opacity: saving || !contactForm.full_name.trim() ? 0.5 : 1 }}>{saving ? 'Saving...' : 'Save'}</button>
                        <button onClick={() => setShowContactForm(false)} style={{ backgroundColor: 'transparent', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.875rem', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {selectedContactIds.size > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', backgroundColor: '#FFF5F5', border: '0.5px solid #FEB2B2', borderRadius: '8px', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: '500', color: '#C53030' }}>{selectedContactIds.size} selected</span>
                      <button onClick={() => setSelectedContactIds(new Set(contacts.map(c => c.id)))} style={{ fontSize: '0.72rem', color: '#1A56A0', background: 'none', border: '1px solid #BFD7F5', borderRadius: '20px', padding: '0.15rem 0.5rem', cursor: 'pointer' }}>Select all {contacts.length}</button>
                      <button onClick={() => setSelectedContactIds(new Set())} style={{ fontSize: '0.72rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '20px', padding: '0.15rem 0.5rem', cursor: 'pointer' }}>Deselect all</button>
                      <button onClick={handleBulkDeleteContacts} style={{ fontSize: '0.72rem', color: '#fff', background: '#C53030', border: 'none', borderRadius: '20px', padding: '0.15rem 0.6rem', cursor: 'pointer', fontWeight: '500' }}>Delete {selectedContactIds.size}</button>
                    </div>
                  )}
                  {contacts.length === 0 && !showContactForm ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#888', backgroundColor: '#fff', borderRadius: '10px', border: '0.5px solid #E8E4E0' }}>
                      <p style={{ margin: 0, fontSize: '0.85rem' }}>No contacts yet.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {contacts.map(contact => {
                        const hasEnrichment = !!enrichedContacts[contact.id];
                        return (
                          <div key={contact.id}
                            style={{ backgroundColor: selectedContactIds.has(contact.id) ? '#FFF5F5' : '#fff', border: '0.5px solid #E8E4E0', borderLeft: `3px solid ${selectedContactIds.has(contact.id) ? '#C53030' : '#276749'}`, borderRadius: '10px', padding: '0.85rem 1rem', cursor: 'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'}
                            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                              <input type="checkbox" checked={selectedContactIds.has(contact.id)} onChange={e => { e.stopPropagation(); const n = new Set(selectedContactIds); if (e.target.checked) n.add(contact.id); else n.delete(contact.id); setSelectedContactIds(n); }} onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }} />
                              <div onClick={() => setSelectedContact(contact.id)} style={{ minWidth: 0, flex: 1, cursor: 'pointer' }}>
                                <p style={{ margin: 0, fontWeight: '500', fontSize: '0.875rem', color: '#1A1A1A' }}>{contact.full_name}</p>
                                {contact.job_title && <p style={{ margin: '0.1rem 0 0', fontSize: '0.78rem', color: '#888' }}>{contact.job_title}</p>}
                                {contact.email && <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: '#AAA' }}>{contact.email}</p>}
                              </div>
                              <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0, alignItems: 'center' }}>
                                {hasEnrichment && <span style={{ fontSize: '0.65rem', color: '#6B46C1', backgroundColor: '#F5F0FF', padding: '0.1rem 0.4rem', borderRadius: '20px' }}>enriched</span>}
                                <button onClick={e => { e.stopPropagation(); handleDeleteContact(contact.id, selectedCompany.id); }}
                                  style={{ fontSize: '0.65rem', color: '#C53030', background: 'none', border: '1px solid #FEB2B2', borderRadius: '20px', padding: '0.1rem 0.4rem', cursor: 'pointer' }}>
                                  delete
                                </button>
                                <span onClick={() => setSelectedContact(contact.id)} style={{ fontSize: '0.72rem', color: '#8B1A1A', cursor: 'pointer' }}>→</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#888', backgroundColor: '#fff', borderRadius: '10px', border: '0.5px solid #E8E4E0' }}>
                  <p style={{ margin: 0, fontSize: '0.85rem' }}>Select a company to view contacts.</p>
                </div>
              )}
            </div>
          </div>
          </div>
        )}
      </div>

      {companyPanel && (
        <CompanyPanel
          company={companyPanel}
          clientId={id}
          sweepMeta={sweepMeta}
          scanningId={detectingSignals}
          onClose={() => setCompanyPanel(null)}
          onContactOpen={(contactId) => {
            setCompanyPanel(null);
            setSelectedContact(contactId);
          }}
          onScan={(company) => handleDetectSignals(company)}
        />
      )}

      {addToBatchOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={closeAddToBatch}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', maxWidth: '480px', width: '90%' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>Add to batch</h2>
            <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#888' }}>{selectedGlobalContactIds.size} contacts selected</p>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.4rem', color: '#4A5568' }}>Channel</div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => changeBatchChannel('linkedin')}
                  style={{ flex: 1, padding: '0.5rem', borderRadius: '6px', border: '1px solid ' + (addToBatchChannel === 'linkedin' ? '#0A66C2' : '#DDD'), background: addToBatchChannel === 'linkedin' ? '#EBF8FF' : '#FFF', color: addToBatchChannel === 'linkedin' ? '#0A66C2' : '#666', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}>
                  LinkedIn
                </button>
                <button onClick={() => changeBatchChannel('email')}
                  style={{ flex: 1, padding: '0.5rem', borderRadius: '6px', border: '1px solid ' + (addToBatchChannel === 'email' ? '#1A56A0' : '#DDD'), background: addToBatchChannel === 'email' ? '#EBF8FF' : '#FFF', color: addToBatchChannel === 'email' ? '#1A56A0' : '#666', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}>
                  Email
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.4rem', color: '#4A5568' }}>Target batch</div>
              {!creatingNewBatch ? (
                <>
                  <select value={selectedBatchId} onChange={e => setSelectedBatchId(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #DDD', fontSize: '0.85rem', background: '#FFF' }}>
                    <option value="">Choose a batch…</option>
                    {availableBatches.map(b => (
                      <option key={b.id} value={b.id}>{b.name} ({b.contact_count} contacts)</option>
                    ))}
                  </select>
                  <button onClick={() => setCreatingNewBatch(true)} style={{ marginTop: '0.5rem', background: 'none', border: 'none', color: '#1A56A0', fontSize: '0.78rem', cursor: 'pointer', padding: 0 }}>+ Create new batch</button>
                </>
              ) : (
                <>
                  <input type="text" value={newBatchName} onChange={e => setNewBatchName(e.target.value)} placeholder="New batch name" autoFocus
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #DDD', fontSize: '0.85rem', boxSizing: 'border-box' }} />
                  {addToBatchChannel === 'email' && (
                    <select value={newBatchDestination} onChange={e => setNewBatchDestination(e.target.value)}
                      style={{ width: '100%', marginTop: '0.5rem', padding: '0.5rem', borderRadius: '6px', border: '1px solid #DDD', fontSize: '0.85rem', background: '#FFF' }}>
                      <option value="pipedrive_csv">Pipedrive (CSV)</option>
                      <option value="hubspot_csv">HubSpot (CSV)</option>
                      <option value="bigin_csv">Zoho Bigin (CSV)</option>
                      <option value="zoho_crm_csv">Zoho CRM (CSV)</option>
                      <option value="salesforce_csv">Salesforce (CSV)</option>
                    </select>
                  )}
                  <button onClick={() => setCreatingNewBatch(false)} style={{ marginTop: '0.5rem', background: 'none', border: 'none', color: '#888', fontSize: '0.78rem', cursor: 'pointer', padding: 0 }}>← Pick existing</button>
                </>
              )}
            </div>

            <p style={{ fontSize: '0.72rem', color: '#888', margin: '0 0 1rem' }}>
              Only contacts with an approved {addToBatchChannel} sequence will be added. Others will be skipped.
            </p>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={closeAddToBatch} disabled={batchSubmitting}
                style={{ background: '#FFF', color: '#333', border: '1px solid #DDD', padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={submitAddToBatch} disabled={batchSubmitting}
                style={{ background: '#1A56A0', color: '#FFF', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: batchSubmitting ? 'not-allowed' : 'pointer', opacity: batchSubmitting ? 0.6 : 1 }}>
                {batchSubmitting ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedContact && (
        <ContactPanel
          contactId={selectedContact}
          clientId={id}
          onClose={() => setSelectedContact(null)}
          onSequence={handleSequence}
          userEmail={user?.primaryEmailAddress?.emailAddress}
          onDelete={async (contactId) => {
            try {
              const token = await getToken();
              const r = await fetch(`${API_BASE}/contacts/${contactId}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
                body: JSON.stringify({ user_email: user?.primaryEmailAddress?.emailAddress })
              });
              if (!r.ok) { setError('Delete failed (' + r.status + ').'); return; }
              setSelectedContact(null);
              await fetchCompanies();
              if (selectedCompany) await fetchContacts(selectedCompany.id);
              const res = await fetch(`${API_BASE}/clients/${id}/contacts?limit=500`);
              setAllContacts(await res.json());
            } catch (e) { setError('Delete failed.'); }
          }}
        />
      )}

      {importPreview && (
        <div onClick={() => !importing && setImportPreview(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '520px', width: '90%', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.15rem', fontWeight: 700, color: '#1A1A1A' }}>Review import</h2>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#4A5568' }}>
              We found {(importPreview.matchBreakdown?.byEmail || 0) + (importPreview.matchBreakdown?.byLinkedin || 0) + (importPreview.matchBreakdown?.byName || 0)} duplicate{((importPreview.matchBreakdown?.byEmail || 0) + (importPreview.matchBreakdown?.byLinkedin || 0) + (importPreview.matchBreakdown?.byName || 0)) === 1 ? '' : 's'} in this file.
            </p>

            <div style={{ background: '#FAFAF8', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1.25rem', fontSize: '0.83rem', color: '#4A5568' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                <span>New companies</span><strong>{importPreview.companiesCreated || 0}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                <span>New contacts</span><strong>{importPreview.contactsCreated || 0}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                <span>Duplicate contacts found</span><strong>{importPreview.contactsSkipped || 0}</strong>
              </div>
              {(importPreview.matchBreakdown?.byEmail || importPreview.matchBreakdown?.byLinkedin || importPreview.matchBreakdown?.byName) ? (
                <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '0.5px solid #E8E4E0', fontSize: '0.78rem', color: '#888' }}>
                  Matched by email: {importPreview.matchBreakdown?.byEmail || 0} · LinkedIn: {importPreview.matchBreakdown?.byLinkedin || 0} · name: {importPreview.matchBreakdown?.byName || 0}
                </div>
              ) : null}
            </div>

            <p style={{ margin: '0 0 0.6rem', fontSize: '0.85rem', fontWeight: 600, color: '#1A1A1A' }}>How should duplicates be handled?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem' }}>
              {[
                { key: 'skip', label: 'Skip duplicates', sub: 'Leave existing contacts untouched. Only insert new ones.' },
                { key: 'fill', label: 'Fill empty fields', sub: 'For matched contacts, fill in fields where the existing value is empty.' },
                { key: 'overwrite', label: 'Overwrite with CSV', sub: 'For matched contacts, replace existing values with CSV values (where CSV has data).' },
              ].map(opt => (
                <label key={opt.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.7rem 0.85rem', border: '1px solid ' + (importStrategy === opt.key ? '#8B1A1A' : '#E8E4E0'), borderRadius: '8px', cursor: 'pointer', background: importStrategy === opt.key ? '#FFF5F5' : '#fff' }}>
                  <input type="radio" name="dedup" value={opt.key} checked={importStrategy === opt.key} onChange={() => setImportStrategy(opt.key)} style={{ marginTop: '0.2rem' }} />
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1A1A1A' }}>{opt.label}</div>
                    <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '0.15rem' }}>{opt.sub}</div>
                  </div>
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button onClick={() => setImportPreview(null)} disabled={importing} style={{ flex: 1, padding: '0.7rem', fontSize: '0.85rem', background: '#fff', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => runImport(importStrategy)} disabled={importing} style={{ flex: 1, padding: '0.7rem', fontSize: '0.85rem', fontWeight: 600, background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '6px', cursor: importing ? 'not-allowed' : 'pointer' }}>
                {importing ? 'Importing...' : 'Confirm import'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Prospects;
